import * as React from "react";
import {
  Card,
  Box,
  Typography,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { gold } from "../../../../getLPTheme";

type Subscription = {
  status?: string;
  is_active?: boolean;
  is_delinquent?: boolean;
  payment_method_id?: string | null;
  last_charged_date?: string | null;
  next_payment_date?: string | null;
  raw?: any;
};

function formatDateTime(iso?: string | null) {
  if (!iso) return "-";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleString("es-AR"); // fecha + hora
}

function formatDateOnly(iso?: string | null) {
  if (!iso) return "-";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleDateString("es-AR"); // solo fecha
}

function methodLabel(pm?: string | null) {
  if (!pm) return "-";
  if (pm === "master") return "Mastercard";
  if (pm === "visa") return "Visa";
  return pm;
}

function statusChip(sub: Subscription) {
  const st = sub?.status || "unknown";

  const baseSx = {
    fontWeight: 800,
    border: "1px solid #2B3139",
    bgcolor: "#0B0F14",
    color: "white",
  };

  if (st === "authorized") return <Chip label="AUTORIZADO" sx={{ ...baseSx, color: gold[300] }} />;
  if (st === "pending") return <Chip label="PENDIENTE" sx={{ ...baseSx }} />;
  if (st === "cancelled") return <Chip label="CANCELADA" sx={{ ...baseSx }} />;
  if (st === "paused") return <Chip label="PAUSADA" sx={{ ...baseSx }} />;
  return <Chip label={st.toUpperCase()} sx={{ ...baseSx }} />;
}

export default function SubscriptionPanel({
  loading,
  subscription,
  showRaw = false,
}: {
  loading: boolean;
  subscription: Subscription | null;
  showRaw?: boolean;
}) {
  if (loading) {
    return (
      <Card
        sx={{
          bgcolor: "#0B0F14",
          color: "#EAECEF",
          borderRadius: 3,
          border: "1px solid #2B3139",
          overflow: "hidden",
          p: 2,
        }}
      >
        <Skeleton variant="text" height={32} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 2 }} />
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card
        sx={{
          bgcolor: "#0B0F14",
          color: "#EAECEF",
          borderRadius: 3,
          border: "1px solid #2B3139",
          overflow: "hidden",
          p: 2,
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 18, color: "white" }}>
          No tenés una suscripción configurada
        </Typography>
        <Typography sx={{ opacity: 0.8, mt: 1, color: "white" }}>
          Para habilitar tu credencial necesitás completar el método de pago desde “Subscribirse”.
        </Typography>
      </Card>
    );
  }

  const enabled = subscription.is_active === true;

  return (
    <Card
      sx={{
        bgcolor: "#0B0F14",
        color: "#EAECEF",
        borderRadius: 3,
        border: "1px solid #2B3139",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 22 }, color: "white" }}>
            Resumen
          </Typography>
          <Typography sx={{ opacity: 0.7, mt: 0.5, color: "white" }}>
            Estado y próximos cobros
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {statusChip(subscription)}
          <Chip
            label={enabled ? "USUARIO HABILITADO" : "USUARIO INHABILITADO"}
            sx={{
              fontWeight: 900,
              bgcolor: enabled ? "rgba(0, 255, 150, 0.08)" : "rgba(255, 80, 80, 0.10)",
              border: "1px solid #2B3139",
              color: enabled ? "rgba(0,255,150,0.9)" : "rgba(255,120,120,0.95)",
            }}
          />
          {subscription.is_delinquent && (
            <Chip
              label="CON DEUDA"
              sx={{
                fontWeight: 900,
                bgcolor: "rgba(255, 180, 0, 0.08)",
                border: "1px solid #2B3139",
                color: "rgba(255, 200, 80, 0.95)",
              }}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#2B3139" }} />

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* ÚLTIMO COBRO */}
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "#0E141B", border: "1px solid #2B3139", borderRadius: 2, p: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 900, color: gold[300] }}>
                Último cobro
              </Typography>
              <Typography sx={{ fontWeight: 900, mt: 0.75, color: "white" }}>
                {formatDateTime(subscription.last_charged_date)}
              </Typography>
            </Card>
          </Grid>

          {/* PRÓXIMO COBRO (solo fecha) */}
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "#0E141B", border: "1px solid #2B3139", borderRadius: 2, p: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 900, color: gold[300] }}>
                Próximo cobro
              </Typography>
              <Typography sx={{ fontWeight: 900, mt: 0.75, color: "white" }}>
                {formatDateOnly(subscription.next_payment_date)}
              </Typography>
            </Card>
          </Grid>

          {/* MÉTODO DE PAGO */}
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: "#0E141B", border: "1px solid #2B3139", borderRadius: 2, p: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 900, color: gold[300] }}>
                Método de pago
              </Typography>
              <Typography sx={{ fontWeight: 900, mt: 0.75, color: "white" }}>
                {methodLabel(subscription.payment_method_id)}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* RAW (solo admin) */}
        {showRaw && (
          <Accordion
            sx={{
              mt: 2,
              bgcolor: "#0E141B",
              border: "1px solid #2B3139",
              borderRadius: 2,
              color: "#EAECEF",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}>
              <Typography sx={{ fontWeight: 900, color: gold[300] }}>
                Ver detalle técnico (raw)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#0B0F14",
                  border: "1px solid #2B3139",
                  overflow: "auto",
                  fontSize: 12,
                  lineHeight: 1.5,
                  maxHeight: 320,
                  color: "white",
                }}
              >
                {JSON.stringify(subscription.raw ?? {}, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Card>
  );
}
